// App Constants
export const APP_NAME = import.meta.env.VITE_APP_NAME || 'BSNL FTTH Support'
export const APP_VERSION = import.meta.env.VITE_APP_VERSION || '1.0.0'

// Routes
export const ROUTES = {
  // Auth
  LOGIN: '/login',
  SPLASH: '/splash',
  
  // Main
  DASHBOARD: '/',
  
  // Inquiries
  INQUIRIES: '/inquiries',
  NEW_INQUIRY: '/inquiries/new',
  INQUIRY_DETAIL: '/inquiries/:id',
  
  // Installations
  INSTALLATIONS: '/installations',
  INSTALLATION_DETAIL: '/installations/:id',
  INSTALLATION_WORKFLOW: '/installations/:id/workflow',
  
  // Installation Stages
  SITE_INSPECTION: '/installations/:id/inspection',
  COST_ESTIMATION: '/installations/:id/estimation',
  CABLE_LAYING: '/installations/:id/cable',
  ONT_INSTALLATION: '/installations/:id/ont',
  CONFIGURATION: '/installations/:id/config',
  TESTING: '/installations/:id/testing',
  CUSTOMER_ACCEPTANCE: '/installations/:id/acceptance',
  INSTALLATION_REPORT: '/installations/:id/report',
  
  // Follow-ups
  FOLLOWUPS: '/followups',
  FOLLOWUP_DETAIL: '/followups/:id',
  FOLLOWUP_VISIT: '/followups/:id/visit',
  QUALITY_VALIDATION: '/followups/:id/quality',
  THEVRA_INTEGRATION: '/followups/:id/thevra',
  
  // Tickets
  TICKETS: '/tickets',
  TICKET_DETAIL: '/tickets/:id',
  NEW_TICKET: '/tickets/new',
  
  // Tasks
  TASKS: '/tasks',
  NEW_TASK: '/tasks/new',
  TASK_DETAIL: '/tasks/:id',

  // Engineers
  ENGINEERS: '/engineers',
  ENGINEER_DETAIL: '/engineers/:id',
  ENGINEER_ASSIGNMENT: '/engineers/assignment',
  
  // Reports
  REPORTS: '/reports',
  ENGINEER_PERFORMANCE: '/reports/engineers',
  
  // Settings
  SETTINGS: '/settings',
  NOTIFICATIONS: '/notifications',
  OFFLINE_SYNC: '/sync',
  PROFILE: '/profile',
  LOGOUT: '/logout',

  // Customers
  CUSTOMERS: '/customers',

  // Network Topology
  NETWORK_TOPOLOGY: '/network-topology'
} as const

// User Roles
export const USER_ROLES = {
  ADMIN: 'admin',
  ENGINEER: 'engineer',
  SUPERVISOR: 'supervisor',
  CUSTOMER: 'customer'
} as const

// Status Maps
export const INQUIRY_STATUS_MAP: Record<string, { label: string; color: string; bg: string }> = {
  new: { label: 'New', color: 'text-blue-700', bg: 'bg-blue-100' },
  assigned: { label: 'Assigned', color: 'text-purple-700', bg: 'bg-purple-100' },
  in_progress: { label: 'In Progress', color: 'text-yellow-700', bg: 'bg-yellow-100' },
  completed: { label: 'Completed', color: 'text-green-700', bg: 'bg-green-100' },
  cancelled: { label: 'Cancelled', color: 'text-gray-700', bg: 'bg-gray-100' }
}

export const INSTALLATION_STATUS_MAP: Record<string, { label: string; color: string; bg: string; icon: string }> = {
  pending: { label: 'Pending', color: 'text-gray-700', bg: 'bg-gray-100', icon: 'Clock' },
  site_inspection: { label: 'Site Inspection', color: 'text-blue-700', bg: 'bg-blue-100', icon: 'Search' },
  cost_estimation: { label: 'Cost Estimation', color: 'text-orange-700', bg: 'bg-orange-100', icon: 'Calculator' },
  cable_laying: { label: 'Cable Laying', color: 'text-purple-700', bg: 'bg-purple-100', icon: 'Cable' },
  ont_installation: { label: 'ONT Installation', color: 'text-indigo-700', bg: 'bg-indigo-100', icon: 'Router' },
  configuration: { label: 'Configuration', color: 'text-cyan-700', bg: 'bg-cyan-100', icon: 'Settings' },
  testing: { label: 'Testing', color: 'text-pink-700', bg: 'bg-pink-100', icon: 'Gauge' },
  customer_acceptance: { label: 'Acceptance', color: 'text-teal-700', bg: 'bg-teal-100', icon: 'CheckCircle' },
  completed: { label: 'Completed', color: 'text-green-700', bg: 'bg-green-100', icon: 'CheckCircle2' },
  rejected: { label: 'Rejected', color: 'text-red-700', bg: 'bg-red-100', icon: 'XCircle' }
}

export const TICKET_STATUS_MAP: Record<string, { label: string; color: string; bg: string }> = {
  open: { label: 'Open', color: 'text-red-700', bg: 'bg-red-100' },
  in_progress: { label: 'In Progress', color: 'text-yellow-700', bg: 'bg-yellow-100' },
  resolved: { label: 'Resolved', color: 'text-green-700', bg: 'bg-green-100' },
  closed: { label: 'Closed', color: 'text-gray-700', bg: 'bg-gray-100' },
  escalated: { label: 'Escalated', color: 'text-purple-700', bg: 'bg-purple-100' }
}

export const PRIORITY_MAP: Record<string, { label: string; color: string; bg: string }> = {
  low: { label: 'Low', color: 'text-gray-700', bg: 'bg-gray-100' },
  medium: { label: 'Medium', color: 'text-blue-700', bg: 'bg-blue-100' },
  high: { label: 'High', color: 'text-orange-700', bg: 'bg-orange-100' },
  urgent: { label: 'Urgent', color: 'text-red-700', bg: 'bg-red-100' }
}

// Installation Stages Order
export const INSTALLATION_STAGES = [
  'pending',
  'site_inspection',
  'cost_estimation',
  'cable_laying',
  'ont_installation',
  'configuration',
  'testing',
  'customer_acceptance',
  'completed'
] as const

// Firebase Collections
export const COLLECTIONS = {
  USERS: 'users',
  INQUIRIES: 'inquiries',
  INSTALLATIONS: 'installations',
  TICKETS: 'tickets',
  FOLLOWUPS: 'followups',
  FEEDBACK: 'feedback',
  ENGINEERS: 'engineers',
  TASKS: 'tasks',
  NOTIFICATIONS: 'notifications',
  REPORTS: 'reports',
  SYNC_QUEUE: 'syncQueue',
  ACTIVITY_LOGS: 'activityLogs',
  CUSTOMERS: 'bsnl_customers',
  BUILDINGS: 'buildings'
} as const

// Storage Paths
export const STORAGE_PATHS = {
  INSTALLATION_PHOTOS: 'installations/photos',
  ONT_PHOTOS: 'installations/ont',
  CABLE_PHOTOS: 'installations/cable',
  TESTING_PHOTOS: 'installations/testing',
  SIGNATURES: 'signatures',
  PROFILE_PHOTOS: 'profiles',
  DOCUMENTS: 'documents'
} as const

// Pagination
export const DEFAULT_PAGE_SIZE = 20
export const MAX_PAGE_SIZE = 100

// Timeouts
export const API_TIMEOUT = 30000
export const SYNC_INTERVAL = 30000 // 30 seconds
export const OFFLINE_RETRY_ATTEMPTS = 3

// SLA Timeframes (in hours)
export const SLA_TIMEFRAMES = {
  INSTALLATION: 72,
  TICKET_RESOLUTION: 24,
  FOLLOWUP: 48,
  QUALITY_CHECK: 168 // 7 days
}

// Validation Constants
export const VALIDATION = {
  PHONE_REGEX: /^[6-9]\d{9}$/,
  PINCODE_REGEX: /^\d{6}$/,
  EMAIL_REGEX: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  MIN_PASSWORD_LENGTH: 6,
  MAX_OTP_ATTEMPTS: 3,
  OTP_EXPIRY_MINUTES: 10
}

// Default Plans
export const DEFAULT_PLANS = [
  { code: 'BHARAT_FIBER_100', name: 'Bharat Fiber Basic', speed: '100 Mbps', data: '1000 GB', price: 599 },
  { code: 'BHARAT_FIBER_200', name: 'Bharat Fiber Plus', speed: '200 Mbps', data: '1500 GB', price: 799 },
  { code: 'BHARAT_FIBER_300', name: 'Bharat Fiber Premium', speed: '300 Mbps', data: '2000 GB', price: 999 },
  { code: 'BHARAT_FIBER_500', name: 'Bharat Fiber Ultra', speed: '500 Mbps', data: '3000 GB', price: 1499 }
]

// Quality Check Parameters
export const QUALITY_PARAMETERS = [
  { id: 'signal_strength', name: 'Signal Strength', unit: 'dBm', min: -25, max: -8 },
  { id: 'download_speed', name: 'Download Speed', unit: 'Mbps', minPercent: 80 },
  { id: 'upload_speed', name: 'Upload Speed', unit: 'Mbps', minPercent: 80 },
  { id: 'ping', name: 'Ping/Latency', unit: 'ms', max: 20 },
  { id: 'packet_loss', name: 'Packet Loss', unit: '%', max: 1 },
  { id: 'wifi_coverage', name: 'WiFi Coverage', unit: '%', min: 90 }
]

// Error Messages
export const ERROR_MESSAGES = {
  GENERIC: 'Something went wrong. Please try again.',
  NETWORK_ERROR: 'Network connection failed. Please check your internet.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  NOT_FOUND: 'The requested resource was not found.',
  VALIDATION_FAILED: 'Please check your input and try again.',
  OFFLINE: 'You are currently offline. Changes will sync when connected.',
  SYNC_FAILED: 'Failed to sync data. Please try again later.',
  PHONE_INVALID: 'Please enter a valid 10-digit mobile number.',
  OTP_INVALID: 'Invalid OTP. Please try again.',
  OTP_EXPIRED: 'OTP has expired. Please request a new one.',
  REQUIRED_FIELD: 'This field is required.'
}

// Success Messages
export const SUCCESS_MESSAGES = {
  LOGIN_SUCCESS: 'Welcome back!',
  LOGOUT_SUCCESS: 'You have been logged out successfully.',
  INQUIRY_CREATED: 'Inquiry has been created successfully.',
  INSTALLATION_UPDATED: 'Installation updated successfully.',
  TICKET_CREATED: 'Ticket created successfully.',
  TICKET_RESOLVED: 'Ticket resolved successfully.',
  FOLLOWUP_COMPLETED: 'Follow-up completed successfully.',
  FEEDBACK_SUBMITTED: 'Thank you for your feedback!',
  SYNC_COMPLETE: 'All data synced successfully.',
  PHOTO_UPLOADED: 'Photo uploaded successfully.',
  SIGNATURE_SAVED: 'Signature saved successfully.'
}
